import { useCallback, useEffect, useRef, useState } from 'react'
import type { Article, Deposant } from '@troc/shared'
import { anciennete, codeFamille, eur, MOIS_FIN_CONTRAT } from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { Signature, signatureEnSvg } from '../composants/Signature'
import { ChoixVendeur } from '../composants/ChoixVendeur'
import { PhotoEtiquette } from '../composants/PhotoEtiquette'
import { imprimerSortie } from '../impression/documents'
import type { SortieImprimable } from '../impression/documents'
import { useScanner } from '../composants/useScanner'
import { useToast } from '../etat/Toast'
import type { ResultatScan } from '../composants/ScannerCamera'

/**
 * Reprises et dons.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Ce n'est pas la boutique qui va vers les gens
 * ────────────────────────────────────────────────────────────────────────────
 *
 * L'écran s'ouvrait sur la liste des clients dont le contrat arrive à terme,
 * comme s'il fallait les rappeler un à un. Le magasin ne travaille pas ainsi :
 * **c'est le client qui se présente**, ses pièces sous le bras. Une liste de
 * dix-huit mille déposants à trier ne l'aide en rien.
 *
 * L'écran s'ouvre donc sur la seule question du comptoir : **qui est là, et
 * qu'est-ce qu'il rapporte ?** Deux façons de répondre — le numéro de la pièce
 * qu'il tend, ou son nom quand il arrive les mains vides.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * On encode une pièce après l'autre, et on voit la pile grandir
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Un client qui vient chercher ses invendus en a rarement un seul. Les numéros
 * s'enchaînent donc dans le même champ et forment une liste sous les yeux, qu'on
 * relit avec lui avant d'aller plus loin. Le premier numéro désigne le client :
 * les suivants doivent être à lui, sinon on le dit — un bon de reprise ne
 * concerne qu'une personne, elle le signe.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Puis vient la partie qui prend du temps
 * ────────────────────────────────────────────────────────────────────────────
 *
 * **Retrouver les pièces dans le magasin.** D'où une liste qu'on coche pendant
 * qu'on marche, et non un bouton « tout rendre ». Une pièce ni vendue ni sur
 * l'étagère se marque **introuvable** : c'est du vol, de la casse ou une erreur
 * d'encodage, et l'écrire vaut mieux que de la noyer dans un « retiré ».
 */

type Etat = 'rendu' | 'introuvable'

export function Retour() {
  const { signaler, alerter } = useToast()
  const [onglet, setOnglet] = useState<'reprise' | 'dons'>('reprise')
  /** Les pièces posées sur le comptoir, dans l'ordre où on les encode. */
  const [enMain, setEnMain] = useState<Article[]>([])
  /** Le client dont la liste complète est ouverte — l'étape d'après. */
  const [choisi, setChoisi] = useState<number | null>(null)

  // La vérification d'un ajout lit la liste courante. Un rappel figé à la
  // création — celui de la caméra, celui de la douchette — ne verrait jamais
  // que la liste vide du premier rendu.
  const listeCourante = useRef<Article[]>(enMain)
  listeCourante.current = enMain

  /**
   * Ajoute une pièce à la liste, ou dit pourquoi c'est impossible.
   *
   * Trois refus, tous rencontrés au comptoir : la pièce n'est plus en rayon,
   * elle y est déjà, ou elle est à quelqu'un d'autre.
   */
  const ajouter = useCallback((article: Article): ResultatScan => {
    const liste = listeCourante.current
    const refuser = (detail: string) => ({ ok: false, libelle: article.ref, detail })

    if (article.statut !== 'en_vente') {
      return refuser(`${article.ref} n’est plus en rayon — déjà vendu, rendu ou retiré.`)
    }
    if (liste.some((a) => a.id === article.id)) {
      return refuser(`${article.ref} est déjà dans la liste.`)
    }
    const proprietaire = liste[0]
    if (proprietaire && proprietaire.deposantId !== article.deposantId) {
      return refuser(
        `${article.ref} appartient à ${article.deposantNom ?? 'un autre client'}, ` +
          `et la reprise en cours est celle de ${proprietaire.deposantNom ?? 'ce client'}. ` +
          `Un bon de reprise ne concerne qu’une personne : terminez celui-ci d’abord.`,
      )
    }

    setEnMain((actuelle) => [...actuelle, article])
    return { ok: true, libelle: article.titre, detail: article.deposantNom ?? undefined }
  }, [])

  const retirer = (id: number) => setEnMain((actuelle) => actuelle.filter((a) => a.id !== id))

  const vider = () => {
    setEnMain([])
    setChoisi(null)
  }

  const outils = (
    <div className="seg" style={{ marginLeft: 'auto' }}>
      <button
        type="button"
        className="seg-opt"
        aria-pressed={onglet === 'reprise'}
        onClick={() => setOnglet('reprise')}
      >
        Reprise
      </button>
      <button
        type="button"
        className="seg-opt"
        aria-pressed={onglet === 'dons'}
        onClick={() => {
          setOnglet('dons')
          vider()
        }}
      >
        Dons à la Croix-Rouge
      </button>
    </div>
  )

  if (onglet === 'dons') {
    return (
      <Ecran titre="Retour" sousTitre="Pièces non réclamées" outils={outils}>
        <Dons onErreur={alerter} onInfo={signaler} />
      </Ecran>
    )
  }

  if (choisi !== null) {
    return (
      <Ecran titre="Retour" sousTitre="Reprise par le client" outils={outils}>
        <Reprise
          deposantId={choisi}
          enMain={enMain}
          onTermine={vider}
          onErreur={alerter}
          onInfo={signaler}
        />
      </Ecran>
    )
  }

  /** La première pièce encodée désigne le client de toute la reprise. */
  const client = enMain[0] ?? null
  const total = enMain.reduce((somme, a) => somme + a.prixActuelCentimes, 0)

  return (
    <Ecran titre="Retour" sousTitre="Le client vient chercher ses pièces" outils={outils}>
      <RechercheRetour
        onArticle={ajouter}
        onClient={(id) => {
          // Chercher un autre client par son nom abandonne la pile en cours :
          // elle appartenait à quelqu'un d'autre.
          setEnMain((actuelle) => (actuelle[0] && actuelle[0].deposantId !== id ? [] : actuelle))
          setChoisi(id)
        }}
        onErreur={alerter}
      />

      <div className="zone-defilante">
        {enMain.length === 0 && (
          <div className="vide">
            Encodez les numéros des pièces que le client rapporte, l’un après l’autre.
            <br />
            Ils s’ajoutent ici et forment la reprise.
            <br />
            <br />
            S’il arrive les mains vides, cherchez-le par son nom : on ouvrira son contrat.
          </div>
        )}

        {client && (
          <div className="preliste">
            <div className="preliste-entete">
              <div>
                <span className="etiquette">Reprise pour</span>
                <div className="titre" style={{ fontSize: 19 }}>
                  {client.deposantNom ?? `Client ${client.deposantId}`}
                </div>
              </div>
              <div className="a-droite">
                <div className="chiffre" style={{ fontSize: 19 }}>
                  {enMain.length} pièce{enMain.length > 1 ? 's' : ''}
                </div>
                <div className="sourdine">{eur(total)} affichés</div>
              </div>
            </div>

            <table className="tableau">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Désignation</th>
                  <th>Famille</th>
                  <th>Déposé</th>
                  <th className="a-droite">Prix</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {enMain.map((a) => (
                  <tr key={a.id}>
                    <td className="ref">{a.ref}</td>
                    <td className="titre">{a.titre}</td>
                    <td className="sourdine">{codeFamille(a.categorie)}</td>
                    <td className="sourdine" style={{ whiteSpace: 'nowrap' }}>
                      {anciennete(a.deposeLe)}
                      {a.mois >= MOIS_FIN_CONTRAT && <strong> · échu</strong>}
                    </td>
                    <td className="a-droite chiffre">{eur(a.prixActuelCentimes)}</td>
                    <td className="a-droite">
                      <button
                        type="button"
                        className="btn btn-fantome"
                        onClick={() => retirer(a.id)}
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {client && (
        <div className="pied-encaissement">
          <div className="panneau-pied" style={{ borderTop: 0 }}>
            <button type="button" className="btn btn-secondaire btn-large" onClick={vider}>
              Tout effacer
            </button>
            <button
              type="button"
              className="btn btn-primaire btn-large"
              style={{ flex: 1 }}
              onClick={() => setChoisi(client.deposantId)}
            >
              Préparer la reprise — {enMain.length} pièce{enMain.length > 1 ? 's' : ''} →
            </button>
          </div>
        </div>
      )}
    </Ecran>
  )
}

/** La liste de préparation d'un client : on coche en parcourant le magasin. */
function Reprise({
  deposantId,
  enMain,
  onTermine,
  onErreur,
  onInfo,
}: {
  deposantId: number
  /** Pièces déjà sur le comptoir : en tête de liste, et déjà cochées. */
  enMain: Article[]
  onTermine: () => void
  onErreur: (m: string) => void
  onInfo: (m: string) => void
}) {
  const [deposant, setDeposant] = useState<Deposant | null>(null)
  const [articles, setArticles] = useState<Article[]>([])
  const [tronquee, setTronquee] = useState(false)
  const [solde, setSolde] = useState(0)
  const [etats, setEtats] = useState<Record<number, Etat>>({})
  const [traces, setTraces] = useState<string[]>([])
  const [envoi, setEnvoi] = useState(false)

  // Les pièces en main ne changent plus une fois la liste ouverte : on les fige
  // pour que le chargement ne se rejoue pas à chaque rendu du parent.
  const initiales = useRef(enMain)

  useEffect(() => {
    api<{ deposant: Deposant; articles: Article[]; solde: { totalCentimes: number }; tronquee?: boolean }>(
      `/retours/client/${deposantId}`,
    )
      .then((r) => {
        setDeposant(r.deposant)
        setSolde(r.solde.totalCentimes)
        setTronquee(Boolean(r.tronquee))

        /**
         * Les pièces en main passent devant, même absentes de la liste.
         *
         * La fiche d'un gros déposant ne rend que ses premières pièces — il y
         * en a qui en ont plus de quinze mille. Sans cette fusion, l'article
         * que le client tend au comptoir pourrait ne pas y figurer du tout.
         */
        const enTete = initiales.current
        const suite = r.articles.filter((a) => !enTete.some((m) => m.id === a.id))
        setArticles([...enTete, ...suite])
        setEtats(Object.fromEntries(enTete.map((a) => [a.id, 'rendu' as Etat])))
      })
      .catch((cause) => onErreur(cause instanceof ErreurApi ? cause.message : 'Chargement impossible.'))
  }, [deposantId, onErreur])

  const basculer = (id: number, etat: Etat) =>
    setEtats((actuels) => {
      const suivant = { ...actuels }
      if (suivant[id] === etat) delete suivant[id]
      else suivant[id] = etat
      return suivant
    })

  const coches = Object.keys(etats).length
  const rendus = Object.values(etats).filter((e) => e === 'rendu').length
  const introuvables = coches - rendus

  const valider = async () => {
    if (envoi || coches === 0) return
    setEnvoi(true)
    try {
      const reponse = await api<{ sortie: SortieImprimable }>('/retours', {
        methode: 'POST',
        corps: {
          deposantId,
          signatureSvg: signatureEnSvg(traces),
          lignes: Object.entries(etats).map(([id, etat]) => ({ articleId: Number(id), etat })),
        },
      })
      imprimerSortie(reponse.sortie)
      onInfo(`Bon de reprise ${reponse.sortie.num} imprimé.`)
      onTermine()
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  if (!deposant) return <div className="vide">Chargement…</div>

  return (
    <>
      <div className="bandeau-deposant">
        <div className="bandeau-deposant-qui">
          <span className="etiquette">Reprise pour</span>
          <span className="nom">
            {deposant.titre ? `${deposant.titre} ` : ''}
            {deposant.nom}
          </span>
        </div>
        <div className="bandeau-deposant-infos">
          <span className="ref">{deposant.ref}</span>
          {(deposant.gsm || deposant.tel) && <span>{deposant.gsm || deposant.tel}</span>}
        </div>
        {solde > 0 && (
          <span className="bandeau-deposant-compte">{eur(solde)} à lui remettre</span>
        )}
      </div>

      <div className="zone-defilante" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div className="note">
          {initiales.current.length > 0 ? (
            <>
              Les <strong>{initiales.current.length} pièce{initiales.current.length > 1 ? 's' : ''}</strong>{' '}
              encodée{initiales.current.length > 1 ? 's' : ''} au comptoir {initiales.current.length > 1 ? 'sont' : 'est'}{' '}
              en tête, déjà cochée{initiales.current.length > 1 ? 's' : ''}. Le reste de son rayon suit :
              cochez ce qu’il emporte aussi.
            </>
          ) : (
            <>
              Cochez les pièces au fur et à mesure que vous les rapportez. Une pièce qui n’est ni
              vendue ni en rayon se marque <strong>introuvable</strong> — elle sortira du stock,
              mais sous ce nom-là.
            </>
          )}
        </div>

        <table className="tableau">
          <thead>
            <tr>
              <th>N°</th>
              <th>Désignation</th>
              <th>Famille</th>
              <th>Déposé</th>
              <th className="a-droite">Prix</th>
              <th className="a-droite">État</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.id} className={etats[a.id] ? 'ligne-corrigee' : undefined}>
                <td className="ref">{a.ref}</td>
                <td className="titre">{a.titre}</td>
                <td className="sourdine">{codeFamille(a.categorie)}</td>
                <td className="sourdine" style={{ whiteSpace: 'nowrap' }}>
                  {anciennete(a.deposeLe)}
                  {a.mois >= MOIS_FIN_CONTRAT && <strong> · échu</strong>}
                </td>
                <td className="a-droite chiffre">{eur(a.prixActuelCentimes)}</td>
                <td className="a-droite" style={{ whiteSpace: 'nowrap' }}>
                  <div className="seg">
                    <button
                      type="button"
                      className="seg-opt"
                      aria-pressed={etats[a.id] === 'rendu'}
                      onClick={() => basculer(a.id, 'rendu')}
                    >
                      Rendu
                    </button>
                    <button
                      type="button"
                      className="seg-opt"
                      aria-pressed={etats[a.id] === 'introuvable'}
                      onClick={() => basculer(a.id, 'introuvable')}
                    >
                      Introuvable
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {articles.length === 0 && <div className="vide">Plus rien en rayon pour ce client.</div>}
        {tronquee && (
          <div className="note">
            Ce client a plus de pièces en rayon que cette liste n’en montre — seules les{' '}
            {articles.length} premières sont affichées. Une pièce absente se retrouve par son
            numéro, au comptoir.
          </div>
        )}

        {coches > 0 && (
          <div className="bloc-fiche">
            <h3 className="bloc-fiche-titre">
              Signature du déposant · {rendus} rendue{rendus > 1 ? 's' : ''}
              {introuvables > 0 && ` · ${introuvables} introuvable${introuvables > 1 ? 's' : ''}`}
            </h3>
            <p className="sourdine" style={{ fontSize: 13, marginTop: 0 }}>
              Il reconnaît avoir repris ces pièces. Le bon s’imprime en deux exemplaires.
            </p>
            <Signature traces={traces} onChange={setTraces} />
          </div>
        )}
      </div>

      <div className="pied-encaissement">
        <ChoixVendeur titre="Qui s’en occupe ?" compact />
        <div className="panneau-pied" style={{ borderTop: 0 }}>
          <button type="button" className="btn btn-secondaire btn-large" onClick={onTermine}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            disabled={envoi || coches === 0}
            onClick={() => void valider()}
          >
            {coches === 0
              ? 'Cochez les pièces reprises'
              : `Enregistrer et imprimer le bon — ${coches} pièce${coches > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </>
  )
}

/** Les pièces jamais réclamées, qui partent à la Croix-Rouge. */
function Dons({
  onErreur,
  onInfo,
}: {
  onErreur: (m: string) => void
  onInfo: (m: string) => void
}) {
  const [articles, setArticles] = useState<Article[]>([])
  const [choisis, setChoisis] = useState<Set<number>>(new Set())
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    api<{ articles: Article[] }>('/dons/candidats')
      .then((r) => {
        setArticles(r.articles)
        setChoisis(new Set())
      })
      .catch(() => undefined)
  }, [])

  useEffect(charger, [charger])

  const basculer = (id: number) =>
    setChoisis((actuels) => {
      const suivant = new Set(actuels)
      if (suivant.has(id)) suivant.delete(id)
      else suivant.add(id)
      return suivant
    })

  const valider = async () => {
    if (envoi || choisis.size === 0) return
    if (
      !window.confirm(
        `Donner ${choisis.size} pièce(s) à la Croix-Rouge ? Cette sortie est définitive et le déposant ne percevra rien.`,
      )
    ) {
      return
    }
    setEnvoi(true)
    try {
      const reponse = await api<{ sortie: SortieImprimable }>('/dons', {
        methode: 'POST',
        corps: { articleIds: [...choisis] },
      })
      imprimerSortie(reponse.sortie)
      onInfo(`Bordereau ${reponse.sortie.num} imprimé.`)
      charger()
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <>
      <div className="zone-defilante" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div className="note">
          Pièces dont le contrat de <strong>{MOIS_FIN_CONTRAT} mois</strong> est dépassé et que le
          déposant n’est pas venu chercher. Le don est définitif : l’article quitte le stock et
          aucune somme n’est due. Appelez le client avant, tant qu’il est encore temps.
        </div>

        {articles.length === 0 && (
          <div className="vide">Aucune pièce n’a dépassé son contrat sans être réclamée.</div>
        )}

        {articles.length > 0 && (
          <table className="tableau">
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className="btn btn-fantome"
                    onClick={() =>
                      setChoisis(
                        choisis.size === articles.length
                          ? new Set()
                          : new Set(articles.map((a) => a.id)),
                      )
                    }
                  >
                    {choisis.size === articles.length ? 'Aucun' : 'Tout'}
                  </button>
                </th>
                <th>N°</th>
                <th>Désignation</th>
                <th>Client</th>
                <th>En rayon depuis</th>
                <th className="a-droite">Prix</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.id} className={choisis.has(a.id) ? 'ligne-corrigee' : undefined}>
                  <td>
                    <label className="case-a-cocher">
                      <input
                        type="checkbox"
                        checked={choisis.has(a.id)}
                        onChange={() => basculer(a.id)}
                      />
                    </label>
                  </td>
                  <td className="ref">{a.ref}</td>
                  <td className="titre">{a.titre}</td>
                  <td className="sourdine">{a.deposantNom}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{anciennete(a.deposeLe)}</td>
                  <td className="a-droite chiffre sourdine">{eur(a.prixActuelCentimes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {articles.length > 0 && (
        <div className="pied-encaissement">
          <ChoixVendeur titre="Qui s’en occupe ?" compact />
          <div className="panneau-pied" style={{ borderTop: 0 }}>
            <button
              type="button"
              className="btn btn-primaire btn-large"
              style={{ flex: 1 }}
              disabled={envoi || choisis.size === 0}
              onClick={() => void valider()}
            >
              {choisis.size === 0
                ? 'Choisissez les pièces à donner'
                : `Donner ${choisis.size} pièce${choisis.size > 1 ? 's' : ''} et imprimer le bordereau`}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Les deux portes d'entrée d'un retour.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Le client arrive avec ses pièces
 * ────────────────────────────────────────────────────────────────────────────
 *
 * C'est le cas courant, et c'est l'objet qui identifie la personne : on encode
 * le numéro de l'étiquette et l'article rejoint la liste, sans quitter le champ
 * — dix pièces s'enchaînent en dix frappes. Le champ garde le curseur, parce
 * qu'entre deux étiquettes on regarde l'objet, pas l'écran.
 *
 * Les étiquettes du rayon ne portent pas de code-barres : le bouton
 * **Photographier** les lit, comme à la caisse. La douchette reste écoutée pour
 * les pièces récemment étiquetées par l'application.
 *
 * Quand il arrive les mains vides, il reste le nom — on ouvre alors son contrat
 * et on cherche avec lui.
 *
 * Un article déjà vendu ou déjà rendu se dit clairement : c'est une question
 * qu'on pose au comptoir plusieurs fois par mois, et « aucun article » n'y
 * répond pas.
 */
function RechercheRetour({
  onArticle,
  onClient,
  onErreur,
}: {
  onArticle: (article: Article) => ResultatScan
  onClient: (deposantId: number) => void
  onErreur: (message: string) => void
}) {
  const [reference, setReference] = useState('')
  const [nom, setNom] = useState('')
  const [pistes, setPistes] = useState<Deposant[]>([])
  const [cherche, setCherche] = useState(false)
  const champRef = useRef<HTMLInputElement>(null)

  /** Résout une référence d'article et l'ajoute à la liste. */
  const parReference = useCallback(
    async (ref: string): Promise<ResultatScan> => {
      const propre = ref.trim()
      if (propre.length < 3) return { ok: false, libelle: propre || 'code illisible' }
      setCherche(true)
      try {
        const r = await api<{ article: Article }>(`/articles/ref/${encodeURIComponent(propre)}`)
        const suite = onArticle(r.article)
        if (suite.ok) setReference('')
        else onErreur(suite.detail ?? suite.libelle)
        return suite
      } catch (cause) {
        const message =
          cause instanceof ErreurApi
            ? cause.statut === 409
              ? `${propre} n’est plus en rayon — déjà vendu ou déjà rendu.`
              : cause.message
            : 'Lecture impossible.'
        onErreur(message)
        return { ok: false, libelle: propre, detail: message }
      } finally {
        setCherche(false)
        // On enchaîne : le curseur revient là où on tape le numéro suivant.
        champRef.current?.focus()
      }
    },
    [onArticle, onErreur],
  )

  // Le lecteur de comptoir est écouté ici aussi : on scanne sans rien viser.
  useScanner((code) => void parReference(code))

  useEffect(() => {
    if (nom.trim().length < 2) {
      setPistes([])
      return
    }
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      api<{ deposants: Deposant[] }>(`/deposants?q=${encodeURIComponent(nom)}`, {
        signal: controleur.signal,
      })
        .then((r) => setPistes(r.deposants.slice(0, 6)))
        .catch(() => undefined)
    }, 220)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [nom])

  return (
    <div className="recherche-retour">
      <div className="champ">
        <label htmlFor="retour-article">Les pièces que le client rapporte</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            id="retour-article"
            ref={champRef}
            className="saisie saisie-grande"
            placeholder="N° d’article, puis Entrée — un après l’autre"
            inputMode="numeric"
            autoFocus
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void parReference(reference)
            }}
          />
          <button
            type="button"
            className="btn btn-primaire btn-large"
            disabled={cherche || reference.trim().length < 3}
            onClick={() => void parReference(reference)}
          >
            Ajouter
          </button>
        </div>
        <PhotoEtiquette
          onArticle={(article) => onArticle(article)}
          className="btn btn-secondaire btn-bloc btn-centre"
          style={{ marginTop: 8 }}
        />
      </div>

      <div className="champ" style={{ position: 'relative' }}>
        <label htmlFor="retour-client">Ou par le nom du client</label>
        <input
          id="retour-client"
          className="saisie saisie-grande"
          placeholder="Nom, n° de client, localité"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
        />
        {pistes.length > 0 && (
          <div className="pistes-clients">
            {pistes.map((d) => (
              <button
                key={d.id}
                type="button"
                className="piste-client"
                onClick={() => {
                  setNom('')
                  setPistes([])
                  onClient(d.id)
                }}
              >
                <span className="titre">{d.nom}</span>
                <span className="sourdine">
                  {d.ref}
                  {d.articlesEnDepot ? ` · ${d.articlesEnDepot} en dépôt` : ' · rien en rayon'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
